/**
 * Game configuration constants
 *
 * Centralized configuration for game timing and behavior.
 * Single source of truth for game-related constants.
 */

/**
 * API key for authenticating internal API requests
 * Used in headers as x-gatsby-app-key
 */
export const API_KEY: string | undefined = process.env.GATSBY_APP_KEY

/**
 * Duration in milliseconds for each guess/question
 * Players have this amount of time to answer before the game advances
 * Default: 30000ms (30 seconds)
 */
export const GUESS_DURATION: number =
  Number(process.env.GATSBY_GUESS_DURATION) || 30000

/**
 * Interval in milliseconds for the long-running safety-net poll on the
 * `game` row. Supabase Realtime is the primary source of state changes —
 * polling exists only to catch payloads that the websocket may have
 * missed (transient CHANNEL_ERROR / silent disconnects).
 *
 * Was 2 s when polling carried the load; bumped to 10 s after Realtime
 * was added (audit M3) to cut full `select` round-trips by 5× while
 * still surfacing dropped events within a single question's window
 * (GUESS_DURATION = 30 s by default).
 */
export const POLL_INTERVAL: number = 10000

/**
 * Maximum number of retries for tick operations
 * When optimistic locking fails due to concurrent updates
 */
export const MAX_TICK_RETRIES: number = 3

/**
 * Game configuration interface for type safety
 */
export interface GameConfig {
  apiKey: string | undefined
  guessDuration: number
  pollInterval: number
  maxTickRetries: number
}

/**
 * Get the current game configuration as an object
 */
export const getGameConfig = (): GameConfig => ({
  apiKey: API_KEY,
  guessDuration: GUESS_DURATION,
  pollInterval: POLL_INTERVAL,
  maxTickRetries: MAX_TICK_RETRIES,
})
